import React, { useState } from 'react';
import { Box, Collapse, IconButton, Typography } from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import type { PendingSession } from './types';

/**
 * Individual session row inside an expanded PatientNarrativeCard.
 *
 * Shows date/time, session type, status badge, and a "Write" button
 * for pending/drafted sessions.
 */
export default function SessionRow({
  session,
  isCurrent,
  onNoteClick,
  dashboardContext,
}: {
  session: PendingSession;
  isCurrent: boolean;
  onNoteClick: (encounterId: string) => void;
  dashboardContext?: PendingSession['dashboardContext'];
}) {
  const statusColor = session.status === 'signed'
    ? 'var(--c2, #66b32b)'
    : session.status === 'drafted'
      ? 'var(--c4-amber, #f59e0b)'
      : 'text.secondary';

  const statusLabel = session.status === 'signed'
    ? 'Signed'
    : session.status === 'drafted'
      ? 'Draft'
      : 'Needs note';

  const [expanded, setExpanded] = useState(false);

  return (
    <Box>
      <Box
      data-testid={`session-row-${session.encounter_id}`}
      aria-current={isCurrent ? 'true' : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 0.75,
        px: 0.5,
        borderRadius: 1,
        bgcolor: isCurrent ? 'rgba(87,189,228,0.08)' : 'transparent',
        '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
        cursor: 'pointer',
      }}
      onClick={() => onNoteClick(session.encounter_id)}
      >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.primary' }}>
          {session.date} {session.time && `at ${session.time}`}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          {session.session_type}
        </Typography>
      </Box>

      {/* Status indicator */}
      <Typography
        variant="caption"
        sx={{
          fontSize: '0.7rem',
          fontWeight: 600,
          color: statusColor,
          flexShrink: 0,
        }}
      >
        {isCurrent ? 'Working now' : statusLabel}
      </Typography>

      {/* Write Note button for pending/drafted sessions */}
        {session.status !== 'signed' && (
          <>
            <Box
              component="button"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onNoteClick(session.encounter_id);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'var(--c2-dark, #3f7e19)',
                background: 'none',
                cursor: 'pointer',
                px: 0.75,
                py: 0.25,
                borderRadius: 1,
                border: '1px solid var(--c2-light, #e2f4d2)',
                flexShrink: 0,
                '&:hover': { bgcolor: 'var(--c2-light, #e2f4d2)' },
              }}
            >
              <EditNoteIcon sx={{ fontSize: 14 }} />
              {session.note_id ? 'Open draft' : 'Write'}
            </Box>
            <IconButton
              size="small"
              aria-label={expanded ? 'Hide session context' : 'Show session context'}
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(prev => !prev);
              }}
              sx={{ p: 0.25 }}
            >
              {expanded ? <RemoveIcon sx={{ fontSize: 16 }} /> : <AddIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </>
        )}
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ px: 1, pb: 0.75, color: 'text.secondary' }}>
          <Typography variant="caption">
            {session.session_type}
            {session.default_template_name ? ` · ${session.default_template_name}` : ''}
          </Typography>
          {dashboardContext && (
            <>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
                {dashboardContext.is_new_client ? 'New client' : dashboardContext.is_returning_client ? 'Returning client' : ''}
                {dashboardContext.appointment_type ? ` · ${dashboardContext.appointment_type}` : ''}
                {dashboardContext.unread_messages_count ? ` · ${dashboardContext.unread_messages_count} unread message${dashboardContext.unread_messages_count === 1 ? '' : 's'}` : ''}
              </Typography>
              {dashboardContext.followups_from_last?.length ? (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
                  Follow-up: {dashboardContext.followups_from_last.slice(0, 2).join('; ')}
                </Typography>
              ) : null}
              {dashboardContext.risk_watch?.length ? (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.25 }}>
                  Watch: {dashboardContext.risk_watch.slice(0, 2).join('; ')}
                </Typography>
              ) : null}
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}
